/**
 * Shared modal-mode resolver for the View/Edit **model** dialogs
 * (turbine models — Story 059; component models — Story 062).
 *
 * Story 074 — this logic existed TWICE, byte-for-byte identical, as
 * `component-models/shared/modalMode.ts#resolveModalMode` and
 * `turbine-models/modal-edit/EditTurbineModelDialog.tsx#resolveMode`. Story
 * 062's header said so outright ("Mirrors Story 059's EditTurbineModelDialog
 * resolver verbatim; copy substitution only"), so a rule change had to be made
 * in two places or the two dialogs would silently disagree. One copy now.
 *
 * Role questions go through `primaryRoleIs` rather than
 * `roles.includes("tenant_admin")`. The platform is single-role, so "is the
 * caller a tenant_admin" means "is tenant_admin the role they RESOLVE to", not
 * "is it somewhere in the transport array" — see `@aeriosiq/auth-client`'s
 * `pickPrimaryRole`. For a single-role user (every user today) the two are
 * identical; for the anomalous multi-role case the collapsed answer is correct.
 *
 * Decision matrix (unchanged — Story 059 §, Story 062 AC11):
 *   - platform_admin  × global|tenant  → Edit · Save + Deactivate
 *   - tenant_admin    × tenant         → Edit · Save + Deactivate
 *   - tenant_admin    × global         → View · banner "global"
 *   - project_manager × tenant         → Edit · Save (NO Deactivate — soft-delete
 *                                        is tenant_admin-only)
 *   - project_manager × global         → View · banner "global"
 *   - any other role  × any            → View · banner "role"
 *   - inactive row OVERRIDES all       → View · Reactivate only, and only for an
 *                                        actor who'd hold soft-delete rights on
 *                                        that scope
 */
import { primaryRoleIs } from "@aeriosiq/auth-client";

export type BannerKey = "global" | "role" | null;

export interface ResolvedModalMode {
  mode: "view" | "edit";
  canSave: boolean;
  canDeactivate: boolean;
  canReactivate: boolean;
  banner: BannerKey;
}

export function resolveModelModalMode(
  roles: readonly string[],
  scope: "global" | "tenant",
  isActive: boolean,
): ResolvedModalMode {
  const isPlatformAdmin = primaryRoleIs(roles, "platform_admin");
  const isTenantAdmin = primaryRoleIs(roles, "tenant_admin");
  const isProjectManager = primaryRoleIs(roles, "project_manager");

  // Inactive rows are read-only regardless of role; only Reactivate is exposed
  // (and only for actors who'd normally hold soft-delete rights on that scope).
  if (!isActive) {
    const canReactivate =
      scope === "tenant" ? isTenantAdmin || isPlatformAdmin : isPlatformAdmin;
    return {
      mode: "view",
      canSave: false,
      canDeactivate: false,
      canReactivate,
      banner: null,
    };
  }

  if (scope === "global") {
    if (isPlatformAdmin) {
      return {
        mode: "edit",
        canSave: true,
        canDeactivate: true,
        canReactivate: false,
        banner: null,
      };
    }
    return {
      mode: "view",
      canSave: false,
      canDeactivate: false,
      canReactivate: false,
      banner: "global",
    };
  }

  // scope === 'tenant'
  if (isTenantAdmin || isPlatformAdmin) {
    return {
      mode: "edit",
      canSave: true,
      canDeactivate: true,
      canReactivate: false,
      banner: null,
    };
  }
  if (isProjectManager) {
    return {
      mode: "edit",
      canSave: true,
      // Soft-delete is tenant_admin-only on tenant rows — Story 062 AC11.
      canDeactivate: false,
      canReactivate: false,
      banner: null,
    };
  }
  return {
    mode: "view",
    canSave: false,
    canDeactivate: false,
    canReactivate: false,
    banner: "role",
  };
}
